<template>
  <!--
    IdleView represents the patient room TV in its normal "Stay product" state.
    This is what the TV shows when no virtual nurse call is active.
    The VCP-43 call overlay appears ON TOP of this content.

    In production this component will be replaced by / wrapped around
    the actual GetWell Stay product rendering.
  -->
  <div class="idle-view">
    <!-- Simulated Stay product background (blurred TV content) -->
    <div class="idle-bg" />
    <div class="idle-bg-overlay" />

    <!-- Stay product placeholder content -->
    <div class="stay-content">
      <div class="stay-header">
        <span class="stay-logo-text">GetWell Network</span>
        <span class="stay-clock">{{ currentTime }}</span>
      </div>

      <div class="stay-welcome">
        <p class="stay-welcome-subtitle">Welcome to</p>
        <h1 class="stay-welcome-title">GetWell Stay</h1>
      </div>
    </div>

    <!-- Connection status (bottom-right, unobtrusive) -->
    <div class="status-badge">
      <span class="status-dot" :class="{ connected: isConnected }" />
      Room {{ roomNumber }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import { signalingService } from '../services/signalingService'
import { apiClient } from '../services/apiClient'
import { useRoomId } from '../composables/useRoomId'
import type { IncomingCallEvent, BreakthroughCallEvent, CallEndedEvent } from '@virtual-care/shared'

const router    = useRouter()
const callStore = useCallStore()
const { roomId, roomNumber } = useRoomId()

const isConnected = ref(false)
const currentTime = ref('')

function updateClock() {
  currentTime.value = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  })
}

let clockInterval: ReturnType<typeof setInterval>

const handleIncomingCall = (event: IncomingCallEvent) => {
  callStore.setIncomingCall(
    event.sessionId,
    event.roomId,
    event.initiatorId,
    event.initiatorName,
    event.callPurpose,
    event.useGenericNurseLabel,
  )
  router.push(`/incoming/${event.sessionId}`)
}

const handleBreakthroughCall = (event: BreakthroughCallEvent) => {
  callStore.setBreakthroughCall(
    event.sessionId,
    event.roomId,
    event.initiatorId,
    event.initiatorName,
  )
  router.push(`/breakthrough/${event.sessionId}`)
}

// Prime audio context on page load so breakthrough calls can start audio
// This must happen early, before any automatic navigation
const primeAudioContext = async () => {
  if (typeof window === 'undefined') {
    console.log('[IdleView] Window not defined, skipping audio priming')
    return
  }

  try {
    const win = window as any
    const AudioContextCtor = win.AudioContext || win.webkitAudioContext
    if (!AudioContextCtor) {
      console.warn('[IdleView] AudioContext not supported')
      return
    }

    // Create shared audio context that persists across navigation
    if (!win.__vcCallAudioContext) {
      win.__vcCallAudioContext = new AudioContextCtor()
      console.log('[IdleView] Created shared audio context')
    }

    const ctx = win.__vcCallAudioContext as AudioContext
    console.log('[IdleView] Audio context state:', ctx.state)
    
    // Try to resume, but don't block on it - autoplay policy might prevent it
    if (ctx.state === 'suspended') {
      // Don't await - just trigger resume and let it fail silently if needed
      ctx.resume().catch((err) => {
        console.log('[IdleView] Audio context resume blocked by autoplay policy (expected):', err.message)
      })
    }

    // Try to play a silent tone (will also fail if no user gesture, but that's OK)
    try {
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      gain.gain.value = 0.00001 // Nearly silent
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start()
      oscillator.stop(ctx.currentTime + 0.01)
      console.log('[IdleView] Audio context primed successfully')
    } catch (err) {
      console.log('[IdleView] Silent tone blocked (expected on first load):', err)
    }
  } catch (error) {
    console.warn('[IdleView] Failed to prime audio context:', error)
    // Don't rethrow - audio priming is not critical for initial connection
  }
}

onMounted(async () => {
  try {
    console.log('[IdleView] Mounting, roomId:', roomId.value)
    
    updateClock()
    clockInterval = setInterval(updateClock, 1000)

    // Prime audio context EARLY before any calls come in
    console.log('[IdleView] Priming audio context...')
    await primeAudioContext()
    console.log('[IdleView] Audio priming complete')

    console.log('[IdleView] Connecting signaling service...')
    signalingService.connect(roomId.value)
    signalingService.on('incoming_call', handleIncomingCall)
    signalingService.on('breakthrough_call', handleBreakthroughCall)
    console.log('[IdleView] Signaling service connected')

    setTimeout(() => { 
      console.log('[IdleView] Connection status set to true')
      isConnected.value = true 
    }, 1000)
  } catch (error) {
    console.error('[IdleView] Error in onMounted:', error)
  }
})

onBeforeUnmount(() => {
  clearInterval(clockInterval)
  signalingService.off('incoming_call', handleIncomingCall)
  signalingService.off('breakthrough_call', handleBreakthroughCall)
})
</script>

<style scoped>
/* ─── Root ─────────────────────────────────────── */
.idle-view {
  height: 100vh;
  width: 100vw;
  position: relative;
  overflow: hidden;
  background: #0d0b10;
}

/* ─── Background: simulated Stay product TV content ── */
.idle-bg {
  position: absolute;
  inset: -20px;
  background-image:
    radial-gradient(ellipse 70% 60% at 15% 45%, rgba(100, 80, 140, 0.5) 0%, transparent 55%),
    radial-gradient(ellipse 50% 40% at 80% 25%, rgba(60, 90, 140, 0.35) 0%, transparent 50%),
    radial-gradient(ellipse 60% 55% at 60% 80%, rgba(80, 50, 100, 0.3) 0%, transparent 55%),
    radial-gradient(ellipse 40% 35% at 40% 50%, rgba(120, 100, 160, 0.25) 0%, transparent 45%),
    linear-gradient(160deg, #0d0b10 0%, #1a1428 40%, #110d1c 70%, #0e1020 100%);
  filter: blur(20px);
  transform: scale(1.05);
}

.idle-bg-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
}

/* ─── Stay product content ──────────────────────── */
.stay-content {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 48px 60px;
}

.stay-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stay-logo-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.stay-clock {
  color: rgba(255, 255, 255, 0.65);
  font-size: 32px;
  font-weight: 300;
  letter-spacing: 2px;
}

.stay-welcome {
  position: absolute;
  bottom: 120px;
  left: 60px;
}

.stay-welcome-subtitle {
  font-size: 28px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 3px;
  text-transform: uppercase;
  margin: 0 0 8px;
}

.stay-welcome-title {
  font-size: 72px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 1px;
  margin: 0;
  line-height: 1.1;
}

/* ─── Status badge (unobtrusive, bottom-right) ──── */
.status-badge {
  position: absolute;
  z-index: 2;
  bottom: 24px;
  right: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 400;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 200, 50, 0.8);
  transition: background 0.4s;
}

.status-dot.connected {
  background: rgba(50, 200, 100, 0.9);
}
</style>
